import {
  EQ_GAIN_BANDS,
  eqGainDb,
  filtersTouched,
  flatFilters,
  foldInstrumentName,
  formatHz,
  hpfHz,
  lpfHz,
  masterFilters,
  styleEqDial,
  vocalEqDial,
  type Dial,
  type InstrumentFilters,
} from "./dials";
import { DEFAULT_MIX_WEIGHTS, lanePhrase, type MixWeights } from "./mixWeights";

export type InstrumentPreset = {
  keys: string[];
  label: string;
  timbre: string;
  level: number;
  filters: InstrumentFilters;
};

/** Named colors that should be obviously audible, not a 50/100 texture bed. */
export const QUICK_INSTRUMENTS = [
  "Bagpipes",
  "Didgeridoo",
  "Sitar",
  "Harmonica",
  "Accordion",
  "Fiddle",
  "Saxophone",
  "Flute",
  "Trumpet",
  "Steel drum",
  "Banjo",
  "Harp",
  "Cello",
  "Organ",
  "Tabla",
  "Koto",
  "Oboe",
  "Ukulele",
] as const;

export const HEALING_INSTRUMENTS = [
  "Singing bowls",
  "Crystal bowls",
  "Handpan",
  "Tuning forks",
  "Wind chimes",
  "Koshi chimes",
  "Tanpura",
  "Gong",
  "Frame drum",
  "Rainstick",
] as const;

export const INSTRUMENT_PRESETS: InstrumentPreset[] = [
  {
    keys: ["bagpipe", "bagpipes", "highlandbagpipes", "pipes"],
    label: "Bagpipes",
    timbre:
      "highland bagpipes with sustained drones and a reedy nasal chanter melody — a lead folk instrument, not a synth pad",
    level: 88,
    filters: { hpf: 38, sub: 15, bass: 28, lowmid: 48, presence: 74, air: 66, lpf: 8 },
  },
  {
    keys: [
      "didgeridoo",
      "digeridoo",
      "didjeridu",
      "didjeridoo",
      "disiridoo",
      "diseridoo",
      "didgeridu",
    ],
    label: "Didgeridoo",
    timbre:
      "circular-breathed didgeridoo drone with vocalized overtones — earthy acoustic drone, not a bass synth",
    level: 88,
    filters: { hpf: 0, sub: 78, bass: 72, lowmid: 58, presence: 30, air: 16, lpf: 62 },
  },
  {
    keys: ["sitar"],
    label: "Sitar",
    timbre: "resonant sitar with buzzing sympathetic strings and a vocal lead phrasing",
    level: 86,
    filters: { hpf: 22, sub: 18, bass: 32, lowmid: 52, presence: 70, air: 64, lpf: 6 },
  },
  {
    keys: ["harmonica", "bluesharp"],
    label: "Harmonica",
    timbre: "close-miked harmonica with breath and reed bite",
    level: 84,
    filters: { hpf: 28, sub: 12, bass: 26, lowmid: 50, presence: 72, air: 58, lpf: 10 },
  },
  {
    keys: ["accordion", "accordian"],
    label: "Accordion",
    timbre: "acoustic accordion bellows and treble reeds, not a synth organ",
    level: 84,
    filters: { hpf: 20, sub: 22, bass: 40, lowmid: 55, presence: 62, air: 48, lpf: 12 },
  },
  {
    keys: ["fiddle", "violin"],
    label: "Fiddle",
    timbre: "bowed fiddle with audible rosin and folk ornament, not a string pad",
    level: 84,
    filters: { hpf: 26, sub: 10, bass: 24, lowmid: 48, presence: 70, air: 62, lpf: 4 },
  },
  {
    keys: ["saxophone", "sax", "altosax", "tenorsax"],
    label: "Saxophone",
    timbre: "expressive saxophone with breath noise and keyed attack",
    level: 86,
    filters: { hpf: 24, sub: 14, bass: 36, lowmid: 52, presence: 68, air: 55, lpf: 8 },
  },
  {
    keys: ["flute"],
    label: "Flute",
    timbre: "acoustic flute with air over the lip plate, not a sine lead",
    level: 82,
    filters: { hpf: 32, sub: 8, bass: 18, lowmid: 40, presence: 66, air: 72, lpf: 4 },
  },
  {
    keys: ["trumpet"],
    label: "Trumpet",
    timbre: "brassy trumpet with lip buzz and fanfare bite",
    level: 84,
    filters: { hpf: 28, sub: 12, bass: 30, lowmid: 46, presence: 74, air: 60, lpf: 6 },
  },
  {
    keys: ["steeldrum", "steelpan"],
    label: "Steel drum",
    timbre: "Caribbean steel pan with metallic ringing notes",
    level: 84,
    filters: { hpf: 24, sub: 16, bass: 34, lowmid: 50, presence: 68, air: 70, lpf: 4 },
  },
  {
    keys: ["banjo"],
    label: "Banjo",
    timbre: "picked banjo with bright skin-head snap and rolling rolls, not a nylon guitar",
    level: 84,
    filters: { hpf: 28, sub: 10, bass: 22, lowmid: 46, presence: 74, air: 66, lpf: 6 },
  },
  {
    keys: ["harp", "concertharp", "pedalharp"],
    label: "Harp",
    timbre: "concert harp with plucked strings and cascading glissandi, not a pad",
    level: 82,
    filters: { hpf: 22, sub: 16, bass: 30, lowmid: 48, presence: 64, air: 70, lpf: 4 },
  },
  {
    keys: ["cello", "violoncello"],
    label: "Cello",
    timbre: "bowed cello with body resonance and bowed attack, not a synth bass",
    level: 84,
    filters: { hpf: 16, sub: 28, bass: 58, lowmid: 55, presence: 62, air: 42, lpf: 8 },
  },
  {
    keys: ["organ", "hammond", "churchorgan", "pipeorgan"],
    label: "Organ",
    timbre: "drawbar / pipe organ with key click and sustained ranks, not a pad",
    level: 82,
    filters: { hpf: 12, sub: 36, bass: 52, lowmid: 50, presence: 58, air: 48, lpf: 10 },
  },
  {
    keys: ["tabla"],
    label: "Tabla",
    timbre: "paired tabla drums with bayan boom and dayan slap, not a generic loop",
    level: 84,
    filters: { hpf: 10, sub: 62, bass: 58, lowmid: 50, presence: 66, air: 44, lpf: 12 },
  },
  {
    keys: ["koto"],
    label: "Koto",
    timbre: "Japanese koto with pentatonic plucked silk and pitch bends, not a harp patch",
    level: 84,
    filters: { hpf: 26, sub: 12, bass: 28, lowmid: 48, presence: 70, air: 64, lpf: 6 },
  },
  {
    keys: ["oboe"],
    label: "Oboe",
    timbre: "double-reed oboe with nasal woodwind bite, not a synth lead",
    level: 82,
    filters: { hpf: 30, sub: 8, bass: 22, lowmid: 50, presence: 72, air: 58, lpf: 8 },
  },
  {
    keys: ["ukulele", "uke"],
    label: "Ukulele",
    timbre: "nylon ukulele with short plucked chords and island strum, not a guitar",
    level: 82,
    filters: { hpf: 30, sub: 8, bass: 24, lowmid: 48, presence: 68, air: 62, lpf: 6 },
  },
  {
    keys: ["singingbowl", "singingbowls", "tibetanbowl", "tibetanbowls"],
    label: "Singing bowls",
    timbre:
      "struck and circled Tibetan singing bowls with long metallic drones and audible beating overtones — not a synth pad",
    level: 90,
    filters: { hpf: 10, sub: 48, bass: 52, lowmid: 62, presence: 44, air: 70, lpf: 6 },
  },
  {
    keys: ["crystalbowl", "crystalbowls", "quartzbowl"],
    label: "Crystal bowls",
    timbre: "quartz crystal singing bowls with pure sine-like partials and very long decay, not a pad patch",
    level: 90,
    filters: { hpf: 16, sub: 28, bass: 36, lowmid: 48, presence: 58, air: 78, lpf: 2 },
  },
  {
    keys: ["handpan", "hangdrum", "hang", "pantam"],
    label: "Handpan",
    timbre: "handpan / hang drum with ding, muted takes, and ringing harmonic steel, not a steel-pan loop",
    level: 86,
    filters: { hpf: 14, sub: 36, bass: 50, lowmid: 55, presence: 62, air: 64, lpf: 8 },
  },
  {
    keys: ["tuningfork", "tuningforks"],
    label: "Tuning forks",
    timbre: "struck tuning forks held to the soundfield with pure Hz beacons, not a beep FX",
    level: 84,
    filters: { hpf: 20, sub: 18, bass: 24, lowmid: 40, presence: 66, air: 74, lpf: 4 },
  },
  {
    keys: ["windchime", "windchimes", "chimes"],
    label: "Wind chimes",
    timbre: "random-air metal wind chimes with overlapping decays, not a MIDI arpeggio",
    level: 78,
    filters: { hpf: 28, sub: 8, bass: 16, lowmid: 32, presence: 60, air: 80, lpf: 0 },
  },
  {
    keys: ["koshichime", "koshichimes", "koshi"],
    label: "Koshi chimes",
    timbre: "Koshi-style bamboo chimes with pentatonic falling tones, not generic bells",
    level: 80,
    filters: { hpf: 24, sub: 10, bass: 18, lowmid: 36, presence: 58, air: 78, lpf: 2 },
  },
  {
    keys: ["tanpura", "tambura", "tampura"],
    label: "Tanpura",
    timbre: "plucked tanpura drone with buzzing jawari overtones, not a synth drone",
    level: 88,
    filters: { hpf: 8, sub: 58, bass: 62, lowmid: 55, presence: 40, air: 36, lpf: 16 },
  },
  {
    keys: ["gong", "tam tam", "tamtam"],
    label: "Gong",
    timbre: "large suspended gong with swell, crash, and long low wash, not a crash cymbal",
    level: 82,
    filters: { hpf: 4, sub: 70, bass: 64, lowmid: 50, presence: 38, air: 48, lpf: 22 },
  },
  {
    keys: ["framedrum", "frame drum", "bodhran"],
    label: "Frame drum",
    timbre: "low frame drum with palm and fingertip, slow ritual pulse, not a kit loop",
    level: 80,
    filters: { hpf: 6, sub: 62, bass: 58, lowmid: 48, presence: 36, air: 28, lpf: 24 },
  },
  {
    keys: ["rainstick", "rain stick"],
    label: "Rainstick",
    timbre: "tilted rainstick pebble cascade, sparse texture, not white noise",
    level: 70,
    filters: { hpf: 22, sub: 20, bass: 28, lowmid: 40, presence: 50, air: 62, lpf: 10 },
  },
];

export function matchInstrumentPreset(name: string): InstrumentPreset | null {
  const fold = foldInstrumentName(name);
  if (!fold) return null;
  const exact =
    INSTRUMENT_PRESETS.find((p) => p.keys.includes(fold) || foldInstrumentName(p.label) === fold) ||
    null;
  if (exact) return exact;
  if (/bagpipe|highlandpipe/.test(fold)) {
    return INSTRUMENT_PRESETS.find((p) => p.label === "Bagpipes") || null;
  }
  if (/didger|digeri|disiri|didjer/.test(fold)) {
    return INSTRUMENT_PRESETS.find((p) => p.label === "Didgeridoo") || null;
  }
  return null;
}

export function instrumentVoice(label: string): string {
  const preset = matchInstrumentPreset(label);
  if (preset) return preset.timbre;
  const name = label.trim() || "this instrument";
  return `a clearly recognizable acoustic ${name} with its true character — do not substitute a generic pop/rock stand-in`;
}

function formatDb(db: number): string {
  if (db === 0) return "0 dB (flat)";
  if (db > 0) return `+${db} dB boost`;
  return `${db} dB cut`;
}

export function eqCurvePhrase(
  subject: string,
  f: InstrumentFilters,
  opts?: { alwaysBands?: boolean },
): string {
  const bits: string[] = [];
  if (f.hpf > 4) bits.push(`high-pass at ${hpfHz(f.hpf)} Hz (12 dB/oct)`);
  else if (opts?.alwaysBands) bits.push("high-pass off");
  for (const band of EQ_GAIN_BANDS) {
    const v = f[band.id] ?? 50;
    const db = eqGainDb(v);
    if (opts?.alwaysBands || Math.abs(db) >= 1) {
      bits.push(`${band.label} at ${formatHz(band.hz)} ${formatDb(db)}`);
    }
  }
  if (f.lpf > 4) bits.push(`low-pass at ${formatHz(lpfHz(f.lpf))}`);
  else if (opts?.alwaysBands) bits.push("low-pass open");
  if (!bits.length) return `${subject}: flat, uncolored curve`;
  return `APPLY ${subject}: ${bits.join("; ")}`;
}

/** Dedicated equalizer paragraph for the compiled caption (Advanced prompt). */
export function compileEqBlock(dials: Dial[], weights?: MixWeights): string {
  const w = weights || DEFAULT_MIX_WEIGHTS;
  const track = eqCurvePhrase(
    "Track equalizer (entire mix — catches every instrument and the vocal)",
    masterFilters(dials),
    { alwaysBands: true },
  );
  const vocalF = vocalEqDial(dials)?.filters ?? flatFilters();
  const vocalEq = eqCurvePhrase("Lead vocal equalizer (voice only, stacked on the track curve)", vocalF, {
    alwaysBands: true,
  });
  const bits: string[] = [
    "EQUALIZER — apply each curve only at its lane weight. A low-weight lane is a tint, not a takeover.",
    lanePhrase("Frequency", w.frequency, track),
    lanePhrase("Vocals EQ", w.vocals, vocalEq),
  ];
  const styleF = styleEqDial(dials)?.filters;
  if (styleF && filtersTouched(styleF)) {
    bits.push(lanePhrase("Style EQ", w.style, eqCurvePhrase("Style tone equalizer (production color)", styleF)));
  }
  if (w.instruments > 0) {
    for (const d of dials.filter((x) => (x.bank === "instruments" || x.custom) && x.value > 0)) {
      if (d.id === "vocaleq" || d.id === "styleeq") continue;
      const f = d.filters;
      if (!f || !filtersTouched(f)) continue;
      if (d.bank === "instruments") {
        bits.push(eqCurvePhrase(`${d.label} equalizer (this instrument only)`, f));
      } else if (d.bank === "vocals" && d.custom) {
        bits.push(eqCurvePhrase(`${d.label} equalizer (this vocal only)`, f));
      }
    }
  }
  return bits.join(" ");
}

export function featuredInstruments(dials: Dial[]): Dial[] {
  return dials.filter(
    (d) => d.bank === "instruments" && d.value > 0 && (Boolean(d.custom) || d.value > 85),
  );
}

export function supportingInstruments(dials: Dial[]): Dial[] {
  return dials.filter((d) => d.bank === "instruments" && !d.custom && d.value > 0 && d.value <= 85);
}

export function omittedInstruments(dials: Dial[]): Dial[] {
  return dials.filter((d) => d.bank === "instruments" && d.value === 0);
}

export function leadInstrument(dials: Dial[], id: string): Dial[] {
  return dials.map((d) => {
    if (d.id === id) return { ...d, value: Math.max(d.value, 88) };
    if (d.bank === "instruments" && d.id !== id && d.value > 30) {
      return { ...d, value: d.custom ? Math.min(d.value, 40) : 22 };
    }
    return d;
  });
}

export function newCustomInstrument(labelRaw: string, uid: (prefix: string) => string): Dial {
  const preset = matchInstrumentPreset(labelRaw);
  const label = preset?.label || labelRaw.trim().slice(0, 48);
  return {
    id: uid("dial"),
    label,
    value: preset?.level ?? 88,
    scope: "all",
    bank: "instruments",
    custom: true,
    filters: preset ? { ...preset.filters } : flatFilters(),
  };
}

export function sameInstrument(a: string, b: string): boolean {
  const fa = foldInstrumentName(a);
  const fb = foldInstrumentName(b);
  if (fa && fa === fb) return true;
  const pa = matchInstrumentPreset(a);
  const pb = matchInstrumentPreset(b);
  return Boolean(pa && pb && pa.label === pb.label);
}
